import { beginBonus, playBonusSpin, startBonus } from '../bonus/bonus-machine';
import { addMoneyCents, assertBetCents, debitMoneyCents } from '../core/money';
import type { RandomSource } from '../core/types';
import { spin as resolveRandomSpin } from '../core/spin-engine';
import { EXTREME_REEL_STRIPS } from '../core/reel-strips';
import type { SpinPresentationResult } from '../rendering/types';
import { appendReplayStep, createReplayDraft } from '../replay/recorder';
import type { ReplayDraftV2, ReplayPresentationMode, ReplayStepV2 } from '../replay/schema';
import type { CycleJournal, CycleReceiptV2 } from '../services/cycle-journal';
import type {
  PersistedRankedRunV2,
  PersistedSessionV2,
  PersistedSettingsV2,
  PersistenceStore,
} from '../services/persistence';
import { AutoSpinController } from './auto-controller';
import type { GameCycleOutcomeV1 } from './cycle-codecs';
import { presentBaseSpin, presentBonusSpin } from './presentation-adapter';
import {
  finishRankedRun as completeRankedRun,
  START_BALANCE_CENTS,
  startRankedRun,
  type CompletedRankedRun,
  type RankedRun,
} from './ranked-run';
import { createReplayStep } from './replay-adapter';
import { completeExtremeRun, extremeRunView, startExtremeRun } from '../extreme/extreme-run';
import {
  EXTREME_BET_CENTS,
  EXTREME_START_BALANCE_CENTS,
  type CompletedExtremeRun,
  type ExtremeRunStateV1,
} from '../extreme/types';
import {
  createFeatureSummary,
  deriveStablePhase,
  type CompletedFeature,
  type GamePhase,
  type GameViewModel,
  type RankedRunView,
} from './runtime-state';

const BET_OPTIONS = new Set([10, 20, 50, 100, 200, 500, 600, 700, 800, 900, 1_000, 2_000]);
const NORMAL_AUTO_DELAY_MS = 950;
const NORMAL_FREE_AUTO_DELAY_MS = 900;
const TURBO_AUTO_DELAY_MS = 260;
const TURBO_FREE_AUTO_DELAY_MS = 250;

export interface GameRendererPort {
  setMode(mode: ReplayPresentationMode): void;
  presentSpin(result: SpinPresentationResult, options: Readonly<{
    turbo: boolean;
    reducedMotion?: boolean;
  }>): Promise<void>;
  cancel(): void;
}

export type GameViewModelListener = (viewModel: GameViewModel) => void;
export type GameIdPurpose = 'cycle' | 'replay' | 'ranked' | 'extreme';

type ScheduledHandle = ReturnType<typeof globalThis.setTimeout>;

export interface AutoSpinScheduler {
  schedule(task: () => void, delayMs: number): ScheduledHandle;
  cancel(handle: ScheduledHandle): void;
}

const DEFAULT_SCHEDULER: AutoSpinScheduler = Object.freeze({
  schedule: (task: () => void, delayMs: number): ScheduledHandle => globalThis.setTimeout(task, delayMs),
  cancel: (handle: ScheduledHandle): void => globalThis.clearTimeout(handle),
});

export interface GameControllerDependencies {
  readonly renderer: GameRendererPort;
  readonly persistence: PersistenceStore;
  readonly journal: CycleJournal<PersistedSessionV2, GameCycleOutcomeV1, ReplayStepV2>;
  readonly random: RandomSource;
  readonly now?: () => number;
  readonly createId?: (purpose: GameIdPurpose) => string;
  readonly scheduler?: AutoSpinScheduler;
  readonly reducedMotion?: boolean;
}

export interface StartRankedInput {
  readonly username: string;
  readonly betCents?: number;
  readonly id?: string;
}

export interface StartExtremeInput {
  readonly username: string;
  readonly id?: string;
}

type PendingReceipt = CycleReceiptV2<PersistedSessionV2, GameCycleOutcomeV1, ReplayStepV2>;

function timestamp(now: () => number): number {
  const value = Math.floor(now());
  if (!Number.isSafeInteger(value) || value <= 0) throw new RangeError('Der Laufzeitstempel ist ungültig.');
  return value;
}

function asPersistedRankedRun(run: RankedRun): PersistedRankedRunV2 {
  return Object.freeze({
    id: run.id,
    active: true,
    name: run.username,
    betCents: run.betCents,
    startBalanceCents: run.startBalanceCents,
    startedAt: run.startedAt,
  });
}

function asRankedRun(run: PersistedRankedRunV2): RankedRun {
  return Object.freeze({
    id: run.id,
    username: run.name,
    betCents: run.betCents,
    startBalanceCents: run.startBalanceCents,
    startedAt: run.startedAt,
  });
}

export class GameController {
  private readonly renderer: GameRendererPort;
  private readonly persistence: PersistenceStore;
  private readonly journal: CycleJournal<PersistedSessionV2, GameCycleOutcomeV1, ReplayStepV2>;
  private readonly random: RandomSource;
  private readonly now: () => number;
  private readonly createExternalId?: (purpose: GameIdPurpose) => string;
  private readonly scheduler: AutoSpinScheduler;
  private readonly reducedMotion: boolean;
  private readonly auto = new AutoSpinController();
  private readonly listeners = new Set<GameViewModelListener>();
  private settings: PersistedSettingsV2;
  private session: PersistedSessionV2;
  private pendingReceipt: PendingReceipt | null = null;
  private phaseOverride: 'spinning' | 'recovering' | null = null;
  private lastWinCents = 0;
  private autoHandle: ScheduledHandle | null = null;
  private idSequence = 0;
  private destroyed = false;

  constructor(dependencies: GameControllerDependencies) {
    this.renderer = dependencies.renderer;
    this.persistence = dependencies.persistence;
    this.journal = dependencies.journal;
    this.random = dependencies.random;
    this.now = dependencies.now ?? Date.now;
    this.createExternalId = dependencies.createId;
    this.scheduler = dependencies.scheduler ?? DEFAULT_SCHEDULER;
    this.reducedMotion = dependencies.reducedMotion ?? false;
    this.settings = this.persistence.loadSettings();
    this.auto.configureStop(this.settings.autoStopEnabled, this.settings.autoStopThresholdX);

    const loaded = this.persistence.loadSession();
    this.session = loaded ?? Object.freeze({
      version: 2,
      revision: 0,
      savedAt: timestamp(this.now),
      balanceCents: START_BALANCE_CENTS,
      betCents: this.settings.betCents,
      rankedRun: null,
      extremeRun: null,
      bonus: null,
      replayDraft: null,
    });
    this.renderer.setMode(this.settings.presentationMode);

    const pending = this.journal.recover();
    if (pending !== null) {
      if (loaded !== null && loaded.revision > pending.stateAfter.revision) {
        this.journal.clear();
      } else {
        this.pendingReceipt = pending;
        this.session = pending.stateAfter;
        this.lastWinCents = pending.outcome.presentation.totalWinCents;
        this.phaseOverride = 'recovering';
      }
    }
    if (this.pendingReceipt === null && !this.requiresDurableSession(this.session)) {
      this.persistence.clearSession();
      this.session = Object.freeze({
        version: 2,
        revision: 0,
        savedAt: timestamp(this.now),
        balanceCents: START_BALANCE_CENTS,
        betCents: this.settings.betCents,
        rankedRun: null,
        extremeRun: null,
        bonus: null,
        replayDraft: null,
      });
    }
  }

  get viewModel(): GameViewModel {
    return this.buildViewModel();
  }

  subscribe(listener: GameViewModelListener): () => void {
    this.assertUsable();
    this.listeners.add(listener);
    listener(this.buildViewModel());
    return () => this.listeners.delete(listener);
  }

  async recover(): Promise<boolean> {
    this.assertUsable();
    const receipt = this.pendingReceipt;
    if (receipt === null) return false;
    this.phaseOverride = 'recovering';
    this.auto.stop();
    this.cancelAutoSchedule();
    this.session = receipt.stateAfter;
    this.lastWinCents = receipt.outcome.presentation.totalWinCents;
    this.emit();
    try {
      this.persistence.saveSession(receipt.stateAfter);
      this.renderer.setMode(this.settings.presentationMode);
      await this.renderer.presentSpin(receipt.outcome.presentation, {
        turbo: this.settings.turbo,
        reducedMotion: this.reducedMotion,
      });
      this.journal.complete(receipt.cycleId);
      this.pendingReceipt = null;
      this.phaseOverride = null;
      this.persistStableSession(receipt.stateAfter);
      this.emit();
      return true;
    } catch (error) {
      this.phaseOverride = 'recovering';
      this.emit();
      throw error;
    }
  }

  setBet(betCents: number): boolean {
    this.assertUsable();
    assertBetCents(betCents);
    if (!BET_OPTIONS.has(betCents)) throw new RangeError('Dieser Einsatz ist nicht verfügbar.');
    if (!this.canChangeBet()) return false;
    const next = this.nextSession(this.session, { betCents });
    this.persistStableSession(next);
    this.session = next;
    this.saveSettings({ betCents });
    this.emit();
    return true;
  }

  setTurbo(enabled: boolean): void {
    this.assertUsable();
    this.saveSettings({ turbo: enabled });
    this.emit();
  }

  toggleTurbo(): boolean {
    this.setTurbo(!this.settings.turbo);
    return this.settings.turbo;
  }

  configureAutoStop(enabled: boolean, thresholdX: number): void {
    this.assertUsable();
    this.auto.configureStop(enabled, thresholdX);
    this.saveSettings({ autoStopEnabled: enabled, autoStopThresholdX: thresholdX });
    this.emit();
  }

  setAutoEnabled(enabled: boolean): boolean {
    this.assertUsable();
    if (!enabled) {
      this.auto.stop();
      this.cancelAutoSchedule();
      this.emit();
      return false;
    }
    const accepted = this.auto.requestEnable(this.isReadyForCycle());
    if (accepted) this.queueAutoSpin(0);
    this.emit();
    return accepted;
  }

  toggleAuto(): boolean {
    return this.setAutoEnabled(!this.auto.isEnabled);
  }

  async spin(): Promise<boolean> {
    this.assertUsable();
    return this.runSpinCycle('manual');
  }

  startFeature(): boolean {
    this.assertUsable();
    if (this.phase !== 'feature-intro' || this.session.bonus === null) return false;
    const started = beginBonus(this.session.bonus);
    const next = this.nextSession(this.session, { bonus: started });
    this.persistStableSession(next);
    this.session = next;
    this.emit();
    return true;
  }

  closeSummary(): CompletedFeature | null {
    this.assertUsable();
    if (this.phase !== 'feature-summary' || this.session.bonus === null) return null;
    const summary = createFeatureSummary(this.session.bonus);
    if (summary === null) return null;
    const completed: CompletedFeature = Object.freeze({
      ...summary,
      replayDraft: this.session.replayDraft,
    });
    const extremeRun = this.session.extremeRun;
    const completedExtremeFeature = extremeRun === null || this.session.replayDraft === null
      ? extremeRun
      : Object.freeze({
        ...extremeRun,
        completedFeatures: Object.freeze([...extremeRun.completedFeatures, Object.freeze({
          featureIndex: extremeRun.completedFeatures.length + 1,
          triggerPaidSpin: extremeRun.paidSpinsCompleted,
          replayDraft: this.session.replayDraft,
        })]),
      });
    const next = this.nextSession(this.session, {
      bonus: null,
      replayDraft: null,
      extremeRun: completedExtremeFeature,
    });
    this.persistStableSession(next);
    this.session = next;
    this.lastWinCents = summary.totalWinCents;
    this.emit();
    return completed;
  }

  startRanked(input: StartRankedInput): RankedRun | null {
    this.assertUsable();
    if (this.phase !== 'idle' || this.auto.isEnabled || this.session.rankedRun !== null || this.session.extremeRun !== null) return null;
    const betCents = input.betCents ?? this.session.betCents;
    assertBetCents(betCents);
    if (!BET_OPTIONS.has(betCents)) throw new RangeError('Dieser Ranked-Einsatz ist nicht verfügbar.');
    const run = startRankedRun({
      id: input.id ?? this.makeId('ranked'),
      username: input.username,
      betCents,
      startedAt: timestamp(this.now),
    });
    const next = this.nextSession(this.session, {
      balanceCents: START_BALANCE_CENTS,
      betCents,
      rankedRun: asPersistedRankedRun(run),
      extremeRun: null,
      bonus: null,
      replayDraft: null,
    });
    this.persistStableSession(next);
    this.session = next;
    this.saveSettings({ betCents });
    this.lastWinCents = 0;
    this.emit();
    return run;
  }

  finishRanked(): CompletedRankedRun | null {
    this.assertUsable();
    if (this.phase !== 'idle' || this.auto.isEnabled || this.session.rankedRun === null) return null;
    const run = asRankedRun(this.session.rankedRun);
    const completed = completeRankedRun(run, this.session.balanceCents, timestamp(this.now));
    const next = this.nextSession(this.session, {
      balanceCents: run.startBalanceCents,
      betCents: run.betCents,
      rankedRun: null,
    });
    this.persistStableSession(next);
    this.session = next;
    this.lastWinCents = 0;
    this.emit();
    return completed;
  }

  cancelRanked(): boolean {
    this.assertUsable();
    if (this.phase !== 'idle' || this.auto.isEnabled || this.session.rankedRun === null) return false;
    const run = this.session.rankedRun;
    const next = this.nextSession(this.session, {
      balanceCents: run.startBalanceCents,
      betCents: run.betCents,
      rankedRun: null,
    });
    this.persistStableSession(next);
    this.session = next;
    this.lastWinCents = 0;
    this.emit();
    return true;
  }

  startExtreme(input: StartExtremeInput): ExtremeRunStateV1 | null {
    this.assertUsable();
    if (this.phase !== 'idle' || this.auto.isEnabled || this.session.rankedRun !== null || this.session.extremeRun !== null) return null;
    const run = startExtremeRun({
      id: input.id ?? this.makeId('extreme'),
      username: input.username,
      returnBalanceCents: this.session.balanceCents,
      returnBetCents: this.session.betCents,
      startedAt: timestamp(this.now),
    });
    const next = this.nextSession(this.session, {
      balanceCents: EXTREME_START_BALANCE_CENTS,
      betCents: EXTREME_BET_CENTS,
      rankedRun: null,
      extremeRun: run,
      bonus: null,
      replayDraft: null,
    });
    this.persistStableSession(next);
    this.session = next;
    this.lastWinCents = 0;
    this.emit();
    return run;
  }

  finishExtreme(): CompletedExtremeRun | null {
    this.assertUsable();
    const run = this.session.extremeRun;
    if (this.phase !== 'extreme-summary' || this.auto.isEnabled || run === null || this.session.bonus !== null) return null;
    const completed = completeExtremeRun(run, this.session.balanceCents, timestamp(this.now));
    const next = this.nextSession(this.session, {
      balanceCents: run.returnBalanceCents,
      betCents: run.returnBetCents,
      extremeRun: null,
      bonus: null,
      replayDraft: null,
    });
    this.persistStableSession(next);
    this.session = next;
    this.lastWinCents = 0;
    this.emit();
    return completed;
  }

  abandonExtreme(): boolean {
    this.assertUsable();
    const run = this.session.extremeRun;
    if (this.phase !== 'idle' || this.auto.isEnabled || run === null) return false;
    const next = this.nextSession(this.session, {
      balanceCents: run.returnBalanceCents,
      betCents: run.returnBetCents,
      extremeRun: null,
      bonus: null,
      replayDraft: null,
    });
    this.persistStableSession(next);
    this.session = next;
    this.lastWinCents = 0;
    this.emit();
    return true;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.auto.stop();
    this.cancelAutoSchedule();
    this.renderer.cancel();
    this.listeners.clear();
  }

  private get phase(): GamePhase {
    return this.phaseOverride ?? deriveStablePhase(this.session);
  }

  private buildViewModel(): GameViewModel {
    const phase = this.phase;
    const isReady = phase === 'idle' || phase === 'feature-ready';
    const baseCanAfford = phase !== 'idle' || this.session.balanceCents >= this.session.betCents;
    const rankedRun = this.createRankedView();
    const extremeRun = this.session.extremeRun === null
      ? null
      : extremeRunView(this.session.extremeRun, this.session.balanceCents, this.session.bonus !== null);
    // A committed receipt already contains the durable post-spin state so a
    // reload cannot lose money, sticky wilds or replay data. During the reveal,
    // however, exposing that state would announce a feature trigger and newly
    // landed sticky wilds before their cards have actually been turned over.
    const visibleBonus = this.phaseOverride !== null && this.pendingReceipt !== null
      ? this.pendingReceipt.stateBefore.bonus
      : this.session.bonus;
    return Object.freeze({
      phase,
      balanceCents: this.session.balanceCents,
      betCents: this.session.betCents,
      lastWinCents: this.lastWinCents,
      turbo: this.settings.turbo,
      presentationMode: this.settings.presentationMode,
      autoEnabled: this.auto.isEnabled,
      bonus: visibleBonus,
      featureSummary: phase === 'feature-summary' ? createFeatureSummary(this.session.bonus) : null,
      rankedRun,
      extremeRun,
      canSpin: isReady && baseCanAfford && !this.auto.isEnabled,
      canChangeBet: this.canChangeBet(),
      canStartFeature: phase === 'feature-intro',
      canCloseSummary: phase === 'feature-summary',
      canToggleAuto: this.auto.isEnabled || (isReady && baseCanAfford),
    });
  }

  private createRankedView(): RankedRunView | null {
    const run = this.session.rankedRun;
    if (run === null) return null;
    return Object.freeze({
      id: run.id,
      username: run.name,
      betCents: run.betCents,
      startBalanceCents: run.startBalanceCents,
      startedAt: run.startedAt,
      currentX: Math.max(0, this.session.balanceCents - run.startBalanceCents) / run.betCents,
    });
  }

  private canChangeBet(): boolean {
    return this.phase === 'idle' && !this.auto.isEnabled
      && this.session.rankedRun === null && this.session.extremeRun === null;
  }

  private isReadyForCycle(): boolean {
    if (this.phaseOverride !== null || this.pendingReceipt !== null) return false;
    const phase = deriveStablePhase(this.session);
    if (phase === 'feature-ready') return true;
    return phase === 'idle' && this.session.balanceCents >= this.session.betCents;
  }

  private async runSpinCycle(source: 'manual' | 'auto'): Promise<boolean> {
    if (!this.isReadyForCycle() || (source === 'manual' && this.auto.isEnabled)
      || (source === 'auto' && !this.auto.isEnabled)) return false;

    this.cancelAutoSchedule();
    const stateBefore = this.session;
    this.phaseOverride = 'spinning';
    this.emit();
    let committed = false;

    try {
      const bonus = stateBefore.bonus;
      if (bonus === null) {
        // The debit is applied to live state before RNG, but becomes durable only
        // together with the immutable result receipt. A pre-commit crash can never
        // lose a bet, and a post-commit recovery can never debit it twice.
        const debitedBalance = debitMoneyCents(stateBefore.balanceCents, stateBefore.betCents);
        this.session = Object.freeze({ ...stateBefore, balanceCents: debitedBalance });
        this.emit();
        const isExtreme = stateBefore.extremeRun !== null;
        const result = resolveRandomSpin({
          mode: 'base',
          betCents: stateBefore.betCents,
          ...(isExtreme ? { reelStrips: EXTREME_REEL_STRIPS } : {}),
        }, this.random);
        const triggeredBonus = startBonus(result.scatterCount);
        const replayDraft = triggeredBonus === null ? null : this.createFeatureReplayDraft(triggeredBonus.totalAwardedSpins);
        const presentation = presentBaseSpin(result);
        const balanceAfterCents = addMoneyCents(debitedBalance, result.totalWinCents);
        const extremeRun = stateBefore.extremeRun === null ? null : Object.freeze({
          ...stateBefore.extremeRun,
          paidSpinsCompleted: stateBefore.extremeRun.paidSpinsCompleted + 1,
          featureCount: stateBefore.extremeRun.featureCount + (triggeredBonus === null ? 0 : 1),
          baseSteps: Object.freeze([...stateBefore.extremeRun.baseSteps, Object.freeze({
            kind: 'base' as const,
            paidSpin: stateBefore.extremeRun.paidSpinsCompleted + 1,
            balanceAfterCents,
            featureTriggered: triggeredBonus !== null,
            presentation,
          })]),
        });
        const stateAfter = this.nextSession(stateBefore, {
          balanceCents: balanceAfterCents,
          extremeRun,
          bonus: triggeredBonus,
          replayDraft,
        });
        const outcome: GameCycleOutcomeV1 = Object.freeze({
          version: 1,
          kind: 'base',
          presentation,
        });
        if (triggeredBonus !== null) {
          this.auto.stop();
          this.cancelAutoSchedule();
        }
        committed = true;
        await this.commitAndPresent(stateBefore, stateAfter, outcome, null);
      } else {
        // Preserve the active Legacy runtime behavior: once POG is selected,
        // its position is overlaid before paylines/scatter are evaluated, so
        // the current spin treats that card as a non-paying POG blocker.
        const result = playBonusSpin(bonus, stateBefore.betCents, this.random, {
          pogEvaluationMode: 'legacy-overlay',
          ...(stateBefore.extremeRun === null ? {} : { enableEgyptianGods: true }),
        });
        const draft = this.usableReplayDraft(stateBefore.replayDraft, bonus.spinsPlayed);
        const stepIndex = draft === null ? result.stateAfter.spinsPlayed : draft.steps.length + 1;
        const replayStep = createReplayStep(result, stepIndex);
        const replayDraft = draft === null ? null : appendReplayStep(draft, replayStep);
        const stateAfter = this.nextSession(stateBefore, {
          balanceCents: addMoneyCents(stateBefore.balanceCents, result.spin.totalWinCents),
          bonus: result.stateAfter,
          replayDraft,
        });
        const outcome: GameCycleOutcomeV1 = Object.freeze({
          version: 1,
          kind: 'bonus',
          presentation: presentBonusSpin(result),
        });
        committed = true;
        await this.commitAndPresent(stateBefore, stateAfter, outcome, replayStep);
      }

      this.queueNextAutoSpin();
      return true;
    } catch (error) {
      if (!committed || this.pendingReceipt === null) {
        this.session = stateBefore;
        this.phaseOverride = null;
      } else {
        this.phaseOverride = 'recovering';
        this.auto.stop();
        this.cancelAutoSchedule();
      }
      this.emit();
      throw error;
    }
  }

  private async commitAndPresent(
    stateBefore: PersistedSessionV2,
    stateAfter: PersistedSessionV2,
    outcome: GameCycleOutcomeV1,
    replayStep: ReplayStepV2 | null,
  ): Promise<void> {
    const cycleId = this.makeId('cycle');
    const receipt = this.journal.commit({
      cycleId,
      mode: outcome.kind === 'base' ? 'base' : 'free',
      committedAt: timestamp(this.now),
      stateBefore,
      stateAfter,
      outcome,
      replayStep,
    });
    this.pendingReceipt = receipt;
    this.session = receipt.stateAfter;
    this.lastWinCents = receipt.outcome.presentation.totalWinCents;
    this.persistence.saveSession(receipt.stateAfter);
    // Economy, bonus transition and replay draft are durable before animation.
    this.emit();
    await this.renderer.presentSpin(receipt.outcome.presentation, {
      turbo: this.settings.turbo,
      reducedMotion: this.reducedMotion,
    });
    this.journal.complete(cycleId);
    this.pendingReceipt = null;
    this.phaseOverride = null;
    this.persistStableSession(receipt.stateAfter);
    this.emit();
  }

  private requiresDurableSession(session: PersistedSessionV2): boolean {
    return session.rankedRun !== null || session.extremeRun !== null
      || session.bonus !== null || session.replayDraft !== null;
  }

  private persistStableSession(session: PersistedSessionV2): void {
    if (this.requiresDurableSession(session)) this.persistence.saveSession(session);
    else this.persistence.clearSession();
  }

  private createFeatureReplayDraft(totalAwarded: number): ReplayDraftV2 {
    return createReplayDraft({
      id: this.makeId('replay'),
      name: this.session.rankedRun?.name ?? this.session.extremeRun?.name ?? this.settings.chatDisplayName,
      betCents: this.session.betCents,
      startedAt: timestamp(this.now),
      presentationMode: this.settings.presentationMode,
      totalAwarded,
    });
  }

  private usableReplayDraft(draft: ReplayDraftV2 | null, spinsPlayed: number): ReplayDraftV2 | null {
    if (draft !== null && draft.steps.length === spinsPlayed) return draft;
    if (draft === null && spinsPlayed === 0) return this.createFeatureReplayDraft(this.session.bonus?.totalAwardedSpins ?? 10);
    return null;
  }

  private queueNextAutoSpin(): void {
    const stablePhase = deriveStablePhase(this.session);
    if (stablePhase === 'feature-summary' || stablePhase === 'extreme-summary') {
      this.auto.stop();
      this.cancelAutoSchedule();
      this.emit();
      return;
    }
    const isFreeSpinMode = stablePhase === 'feature-ready';
    const shouldQueue = this.auto.shouldQueueNext({
      isSafeToStart: this.isReadyForCycle(),
      isFreeSpinMode,
      lastWinCents: this.lastWinCents,
      betCents: this.session.betCents,
    });
    if (!shouldQueue) {
      this.cancelAutoSchedule();
      this.emit();
      return;
    }
    const delay = this.settings.turbo
      ? (isFreeSpinMode ? TURBO_FREE_AUTO_DELAY_MS : TURBO_AUTO_DELAY_MS)
      : (isFreeSpinMode ? NORMAL_FREE_AUTO_DELAY_MS : NORMAL_AUTO_DELAY_MS);
    this.queueAutoSpin(delay);
  }

  private queueAutoSpin(delayMs: number): void {
    if (this.autoHandle !== null || !this.auto.isEnabled) return;
    this.autoHandle = this.scheduler.schedule(() => {
      this.autoHandle = null;
      void this.runSpinCycle('auto').catch(() => {
        this.auto.stop();
        this.cancelAutoSchedule();
        this.emit();
      });
    }, delayMs);
  }

  private cancelAutoSchedule(): void {
    if (this.autoHandle === null) return;
    this.scheduler.cancel(this.autoHandle);
    this.autoHandle = null;
  }

  private saveSettings(patch: Partial<Omit<PersistedSettingsV2, 'version'>>): void {
    const next = Object.freeze({ ...this.settings, ...patch, version: 2 as const });
    this.persistence.saveSettings(next);
    this.settings = next;
  }

  private nextSession(
    current: PersistedSessionV2,
    patch: Partial<Omit<PersistedSessionV2, 'version' | 'revision' | 'savedAt'>>,
  ): PersistedSessionV2 {
    return Object.freeze({
      ...current,
      ...patch,
      version: 2,
      revision: current.revision + 1,
      savedAt: timestamp(this.now),
    });
  }

  private makeId(purpose: GameIdPurpose): string {
    const id = this.createExternalId?.(purpose)
      ?? `${purpose}-${timestamp(this.now)}-${++this.idSequence}`;
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(id)) throw new RangeError('Die erzeugte ID ist ungültig.');
    return id;
  }

  private emit(): void {
    if (this.destroyed) return;
    const viewModel = this.buildViewModel();
    this.listeners.forEach((listener) => listener(viewModel));
  }

  private assertUsable(): void {
    if (this.destroyed) throw new Error('Der GameController wurde bereits beendet.');
  }
}
